import type {
  EditorState,
  TrackType,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import { editableTree, itemOwner } from "./group-split";
import { placeTimelineSelection, type PlacementResult } from "./timeline-place";
import { openPlacementSpace } from "./placement-space";
import { orderTrackZones, trackKind, type TrackKind } from "./track-layout";
import { mergeTimeRanges } from "./timeline-ripple";

export type DropTarget =
  | { trackId: string }
  | { newTrack: { id: string; index: number; kind: TrackKind } };
/** Pointer coordinates refer to the old timeline; numeric start fields refer to the final timeline. */
export function pointerDropFrame(
  state: EditorState,
  ids: readonly string[],
  primaryId: string,
  raw: number,
  target: DropTarget,
): number {
  const primary = state.undoableState.items[primaryId],
    own = itemOwner(state, primaryId);
  if (
    !primary ||
    !own ||
    !own.track.magnetic ||
    !("trackId" in target) ||
    target.trackId !== own.track.id
  )
    return raw;
  const local = [
    ...new Set(ids.includes(primaryId) ? ids : [primaryId]),
  ].filter((id) => itemOwner(state, id)?.track.id === own.track.id);
  const ranges = mergeTimeRanges(
    local.map((id) => {
      const i = state.undoableState.items[id]!;
      return { start: i.from, end: i.from + i.durationInFrames };
    }),
  );
  const first = ranges[0]!.start,
    anchor = Math.max(0, first + raw - primary.from);
  const collapsed =
    anchor -
    ranges.reduce(
      (sum, r) => sum + Math.max(0, Math.min(anchor, r.end) - r.start),
      0,
    );
  const remainingEnd =
    Math.max(
      0,
      ...own.track.items.map((id) => {
        const item = state.undoableState.items[id]!;
        return item.from + item.durationInFrames;
      }),
    ) - ranges.reduce((sum, r) => sum + r.end - r.start, 0);
  return Math.max(0, Math.min(collapsed, remainingEnd) + primary.from - first);
}
/** Horizontal moves use the reorder command. A layer reassignment lifts without deleting time from its old track. */
export function dropTimelineSelection(
  state: EditorState,
  ids: readonly string[],
  primaryId: string,
  from: number,
  target: DropTarget,
): PlacementResult {
  const fail = (
    reason: Extract<PlacementResult, { ok: false }>["reason"],
  ): PlacementResult => ({ ok: false, state, reason });
  if (!Number.isSafeInteger(from) || from < 0) return fail("invalid-position");
  const owner = itemOwner(state, primaryId),
    primary = state.undoableState.items[primaryId];
  if (!owner || !primary) return fail("missing-item");
  if (owner.track.locked) return fail("locked");
  if ("trackId" in target && target.trackId === owner.track.id)
    return placeTimelineSelection(state, ids, primaryId, from);
  if (owner.parentId) return fail("invalid-tree");
  let tracks = state.undoableState.tracks;
  if ("newTrack" in target) {
    const n = target.newTrack;
    if (
      !Number.isSafeInteger(n.index) ||
      n.index < 0 ||
      n.index > tracks.length ||
      tracks.some((t) => t.id === n.id) ||
      n.kind !== trackKind(owner.track)
    )
      return fail("invalid-position");
    const created: TrackType = {
      id: n.id,
      items: [],
      zone: n.kind === "audio" ? "audio" : "visual",
      locked: false,
      hidden: false,
      muted: false,
      magnetic: n.kind === "video",
      metadata: { type: n.kind, isMainTrack: false, trackType: null },
    };
    tracks = orderTrackZones([
      ...tracks.slice(0, n.index),
      created,
      ...tracks.slice(n.index),
    ]);
  }
  const targetId = "trackId" in target ? target.trackId : target.newTrack.id;
  const destination = tracks.find((t) => t.id === targetId);
  if (!destination) return fail("missing-item");
  if (destination.locked) return fail("locked");
  if (trackKind(destination) !== trackKind(owner.track))
    return fail("invalid-position");
  const selected = [
    ...new Set(ids.includes(primaryId) ? ids : [primaryId]),
  ].filter((id) => {
    const own = itemOwner(state, id);
    return own && !own.track.locked;
  });
  const roots = selected.filter((id) => {
    let own = itemOwner(state, id);
    while (own?.parentId) {
      if (selected.includes(own.parentId)) return false;
      own = itemOwner(state, own.parentId);
    }
    return true;
  });
  if (
    roots.some(
      (id) => !editableTree(state, id) || itemOwner(state, id)?.parentId,
    )
  )
    return fail("invalid-tree");
  const delta = from - primary.from;
  if (roots.some((id) => state.undoableState.items[id]!.from + delta < 0))
    return fail("invalid-position");
  const assignments = new Map(
    roots.map((id) => [
      id,
      itemOwner(state, id)!.track.id === owner.track.id
        ? destination.id
        : itemOwner(state, id)!.track.id,
    ]),
  );
  const moving = new Set(roots);
  let lifted: EditorState = {
    ...state,
    undoableState: {
      ...state.undoableState,
      tracks: tracks.map((t) => ({
        ...t,
        items: t.items.filter((id) => !moving.has(id)),
      })),
    },
  };
  const opened = openPlacementSpace(
    lifted,
    roots.map((id) => ({
      trackId: assignments.get(id)!,
      from: state.undoableState.items[id]!.from + delta,
      duration: state.undoableState.items[id]!.durationInFrames,
    })),
    destination.id,
  );
  if (!opened.ok) return fail("target-occupied");
  lifted = opened.state;
  const items = { ...lifted.undoableState.items },
    moved = new Map<string, string>();
  const shift = (id: string, trackId: string) => {
    const source = state.undoableState.items[id]!;
    if (moved.has(id)) return;
    moved.set(id, trackId);
    items[id] = { ...source, from: source.from + delta };
    if (source.type === "group")
      source.children.forEach((child) => shift(child, trackId));
  };
  for (const id of roots) shift(id, assignments.get(id)!);
  const nextTracks = lifted.undoableState.tracks.map((track) => {
    const added = roots.filter((id) => assignments.get(id) === track.id);
    return added.length
      ? {
          ...track,
          items: [...track.items, ...added].sort(
            (a, b) => items[a]!.from - items[b]!.from,
          ),
        }
      : track;
  });
  for (const id of roots) {
    const item = items[id]!,
      track = nextTracks.find((t) => t.id === assignments.get(id))!;
    if (
      track.items.some((other) => {
        const neighbor = items[other]!;
        return (
          !moving.has(other) &&
          item.from < neighbor.from + neighbor.durationInFrames &&
          neighbor.from < item.from + item.durationInFrames
        );
      })
    )
      return fail("target-occupied");
  }
  const transitions = { ...lifted.undoableState.transitions };
  for (const [id, t] of Object.entries(state.undoableState.transitions)) {
    const a = moved.get(t.sourceItemId),
      b = moved.get(t.targetItemId);
    if (a && a === b)
      transitions[id] = { ...t, from: t.from + delta, trackId: a };
    else if (a || b) delete transitions[id];
  }
  return {
    ok: true,
    state: {
      ...lifted,
      timelineTimeEditApplied: true,
      selectedItems: roots,
      undoableState: {
        ...lifted.undoableState,
        tracks: nextTracks,
        items,
        transitions,
      },
    },
  };
}
